import { useEffect, useMemo, useState } from "react";
import { Box, Container, Typography, CircularProgress, Button } from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import GalleryImageViewer from "../Admin/Gallery/GalleryImageViewer";

function HomeGallerySection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [galleries, setGalleries] = useState([]);
  const [selectedGalleryId, setSelectedGalleryId] = useState("");
  const [images, setImages] = useState([]);
  const [loadingGalleries, setLoadingGalleries] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const visibleLimit = isMobile ? 4 : 6;

  const DARK_GREEN = "#052e16";
  const GREEN = "#064e2a";
  const MID_GREEN = "#15803d";
  const SOFT_GREEN = "#f4fbf6";
  const LIGHT_GREEN = "#e8f5ec";

  useEffect(() => {
    loadGalleries();
  }, []);

  useEffect(() => {
    if (!selectedGalleryId) return;
    loadImages(selectedGalleryId);
  }, [selectedGalleryId]);

  const selectedGallery = useMemo(() => {
    return galleries.find((gallery) => String(gallery.id) === String(selectedGalleryId)) || null;
  }, [galleries, selectedGalleryId]);

  const visibleImages = useMemo(() => {
    return images.slice(0, visibleLimit);
  }, [images, visibleLimit]);

  const loadGalleries = async () => {
    setLoadingGalleries(true);

    try {
      const res = await fetch("/gallery/public/list/");
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];

      setGalleries(list);

      if (list.length) {
        setSelectedGalleryId(String(list[0].id));
      }
    } catch (error) {
      console.error(error);
      setGalleries([]);
      setSelectedGalleryId("");
    } finally {
      setLoadingGalleries(false);
    }
  };

  const loadImages = async (galleryId) => {
    setLoadingImages(true);

    try {
      const res = await fetch(`/gallery/public/images/${galleryId}/`);
      const data = await res.json();
      setImages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const openViewer = (index) => {
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
  };

  if (loadingGalleries) {
    return (
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          background: "linear-gradient(180deg, #ffffff 0%, #f4fbf6 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress sx={{ color: GREEN }} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!galleries.length) {
    return null;
  }

  const mobileColumns =
    visibleImages.length === 1 ? "1fr" : visibleImages.length === 2 ? "1fr 1fr" : "1fr 1fr";

  return (
    <>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 6, md: 8 },
          background:
            "linear-gradient(180deg, #ffffff 0%, #f4fbf6 48%, #e8f5ec 100%)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 360,
            height: 360,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(6,78,42,0.11), transparent 70%)",
            top: -150,
            left: -120,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            width: 420,
            height: 420,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(21,128,61,0.11), transparent 70%)",
            bottom: -190,
            right: -140,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            sx={{
              textAlign: "center",
              mb: 0.8,
              fontWeight: 900,
              fontSize: { xs: "0.78rem", md: "0.9rem" },
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: GREEN,
            }}
          >
            Liga Deportiva Cantonal Mejía
          </Typography>

          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              mb: { xs: 1.2, md: 1.5 },
              fontWeight: 950,
              fontSize: { xs: "1.85rem", md: "2.55rem" },
              color: DARK_GREEN,
              lineHeight: 1.12,
            }}
          >
            {selectedGallery?.name || "Galería"}
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              mb: { xs: 2.8, md: 3.2 },
              color: "#3f5f4b",
              fontWeight: 600,
              fontSize: { xs: "0.96rem", md: "1.05rem" },
            }}
          >
            Revive los mejores momentos del torneo de fútbol
          </Typography>

          <Box
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(6,78,42,0.14)",
              backgroundColor: "#fff",
              p: { xs: 1.2, sm: 1.5 },
              mb: { xs: 3, md: 4 },
              boxShadow: "0 18px 44px rgba(6,78,42,0.10)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                mb: 1.2,
                px: 0.4,
              }}
            >
              <PhotoLibraryRoundedIcon sx={{ color: GREEN, fontSize: 20 }} />
              <Typography
                sx={{
                  fontWeight: 900,
                  color: DARK_GREEN,
                  fontSize: ".95rem",
                }}
              >
                Selecciona una galería
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.2,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(6,78,42,0.22)",
                  borderRadius: "999px",
                },
              }}
            >
              {galleries.map((gallery) => {
                const active = String(selectedGalleryId) === String(gallery.id);

                return (
                  <Button
                    key={gallery.id}
                    onClick={() => setSelectedGalleryId(String(gallery.id))}
                    variant={active ? "contained" : "outlined"}
                    sx={{
                      minWidth: { xs: 150, sm: 180 },
                      height: 46,
                      borderRadius: "14px",
                      px: 2.2,
                      flexShrink: 0,
                      fontWeight: 900,
                      textTransform: "none",
                      borderColor: active ? GREEN : "rgba(6,78,42,0.22)",
                      color: active ? "#fff" : GREEN,
                      backgroundColor: active ? GREEN : "#fff",
                      whiteSpace: "nowrap",
                      boxShadow: active ? "0 10px 24px rgba(6,78,42,0.22)" : "none",
                      "&:hover": {
                        backgroundColor: active ? DARK_GREEN : SOFT_GREEN,
                        borderColor: DARK_GREEN,
                        color: active ? "#fff" : DARK_GREEN,
                      },
                    }}
                  >
                    {gallery.name}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {loadingImages ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress sx={{ color: GREEN }} />
            </Box>
          ) : !visibleImages.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <Typography
                sx={{
                  color: "#3f5f4b",
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                No hay imágenes disponibles en esta galería.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: mobileColumns,
                gap: 0.7,
              }}
            >
              {visibleImages.map((img, index) => (
                <Box
                  key={img.id}
                  onClick={() => openViewer(index)}
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: "16px",
                    cursor: "zoom-in",
                    aspectRatio: "1 / 1",
                    boxShadow: "0 12px 30px rgba(6,78,42,0.16)",
                    border: "1px solid rgba(6,78,42,0.10)",
                  }}
                >
                  <Box
                    component="img"
                    src={img.image}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />

                  {index === 0 && (
                    <>
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(180deg, rgba(0,0,0,0.00) 45%, rgba(5,46,22,0.52) 100%)",
                          pointerEvents: "none",
                        }}
                      />

                      <Box
                        sx={{
                          position: "absolute",
                          left: 10,
                          bottom: 10,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.7,
                          px: 1.2,
                          py: 0.7,
                          borderRadius: "999px",
                          backgroundColor: "rgba(255,255,255,0.18)",
                          color: "#fff",
                          backdropFilter: "blur(10px)",
                          boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                          pointerEvents: "none",
                        }}
                      >
                        <TouchAppRoundedIcon sx={{ fontSize: 20 }} />
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 800,
                            lineHeight: 1,
                            letterSpacing: "0.01em",
                            color: "#fff",
                          }}
                        >
                          Toca para ampliar
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box className="row g-3 justify-content-center">
              {visibleImages.map((img, index) => (
                <Box key={img.id} className="col-6 col-md-4 col-lg-3">
                  <Box
                    onClick={() => openViewer(index)}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: "18px",
                      cursor: "zoom-in",
                      aspectRatio: "1 / 1",
                      boxShadow: "0 14px 34px rgba(6,78,42,0.13)",
                      border: "1px solid rgba(6,78,42,0.10)",
                      transition: "transform 0.25s ease, box-shadow 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 20px 46px rgba(6,78,42,0.20)",
                      },
                      "&:hover .home-gallery-image": {
                        transform: "scale(1.06)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={img.image}
                      alt=""
                      className="home-gallery-image"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        transition: "transform 0.35s ease",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 3, md: 4 } }}>
            <Button
              variant="contained"
              onClick={() => navigate("/galeria")}
              sx={{
                px: 3.8,
                height: 46,
                borderRadius: "14px",
                fontWeight: 900,
                textTransform: "none",
                backgroundColor: GREEN,
                color: "#fff",
                boxShadow: "0 14px 30px rgba(6,78,42,0.24)",
                "&:hover": {
                  backgroundColor: DARK_GREEN,
                  transform: "scale(1.05)",
                  boxShadow: "0 18px 38px rgba(6,78,42,0.30)",
                },
              }}
            >
              Ver galería completa
            </Button>
          </Box>
        </Container>
      </Box>

      <GalleryImageViewer
        open={viewerOpen}
        images={visibleImages}
        currentIndex={currentIndex}
        onClose={closeViewer}
        onChangeIndex={setCurrentIndex}
        galleryName={selectedGallery?.name || "Galería"}
      />
    </>
  );
}

export default HomeGallerySection;